import { DeviceType } from "@domain/product/monitoring/device";
import { Family } from "@domain/product/monitoring/family/family";
import { FamilyStep, StepSelectionType } from "@domain/product/monitoring/family/family-step";
import { FamilyStepOption, SubType } from "@domain/product/monitoring/family/family-step-option";
import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import {
  CentralStationsSelectorType,
  PatientMonitorSelectorType,
  TelemetrySelectorType,
} from "@domain/project/monitoring-selector-types.enum";

export enum FamilyVariantMode {
  NO_OPTIONS = "NO_OPTIONS",
  SINGLE_OPTION = "SINGLE_OPTION",
  MULTIPLE_OPTIONS = "MULTIPLE_OPTIONS",
}

export type FamilyStepMap = Record<string, FamilyStep | undefined>;

const MONITORING_CATEGORY_DATA_TYPES: Record<MonitoringCategory, string> = {
  [MonitoringCategory.PATIENT_MONITORS]: $localize`:@@global.patientMonitors:Patient Monitors`,
  [MonitoringCategory.TELEMETRY]: $localize`:@@global.telemetry:Telemetry`,
  [MonitoringCategory.CENTRAL_STATIONS]: $localize`:@@global.centralStations:Central Stations`,
  [MonitoringCategory.DISPLAYS]: $localize`:@@global.displays:Displays`,
  [MonitoringCategory.KVMS]: $localize`:@@global.kvms:KVMs`,
  [MonitoringCategory.UTILITY]: $localize`:@@global.utility:Utility`,
};

export function getMonitoringCategoryDataType(category: MonitoringCategory): string {
  return MONITORING_CATEGORY_DATA_TYPES[category];
}

export type FamilyStepDeviceTypeDefinition = Partial<Record<PatientMonitorSelectorType, readonly DeviceType[]>> &
  Partial<Record<TelemetrySelectorType, readonly DeviceType[]>> &
  Partial<Record<CentralStationsSelectorType, readonly DeviceType[]>>;

export const FAMILY_STEP_DEVICE_TYPES_BY_CATEGORY: Partial<Record<MonitoringCategory, FamilyStepDeviceTypeDefinition>> = {
  [MonitoringCategory.PATIENT_MONITORS]: {
    [PatientMonitorSelectorType.FAMILY]: [DeviceType.PM_BASE],
    [PatientMonitorSelectorType.SENSOR]: [DeviceType.PM_SP02, DeviceType.PM_SPO2],
    [PatientMonitorSelectorType.MOUNTING_GROUP]: [DeviceType.PM_M],
    [PatientMonitorSelectorType.MOUNTING_ARM]: [DeviceType.PM_M],
  },
  [MonitoringCategory.TELEMETRY]: {
    [TelemetrySelectorType.FAMILY]: [DeviceType.TLM_BASE],
    [TelemetrySelectorType.SENSOR]: [DeviceType.TLM_SPO2],
    [TelemetrySelectorType.ADDITIONAL_BATTERIES]: [DeviceType.TLM_A],
  },
  [MonitoringCategory.CENTRAL_STATIONS]: {
    [CentralStationsSelectorType.FAMILY]: [DeviceType.CS_BASE],
    [CentralStationsSelectorType.LICENCE]: [DeviceType.CS_LICENCE_M300, DeviceType.CS_LICENCE],
    [CentralStationsSelectorType.ADDITIONAL_LICENCES]: [DeviceType.CS_LICENCE_M300, DeviceType.CS_LICENCE],
    [CentralStationsSelectorType.ACCESSORIES]: [DeviceType.CS_A],
  },
};

export function getFamilyVariantDeviceTypes(category: MonitoringCategory): readonly DeviceType[] | undefined {
  const definition = FAMILY_STEP_DEVICE_TYPES_BY_CATEGORY[category];

  if (category === MonitoringCategory.PATIENT_MONITORS) return definition?.[PatientMonitorSelectorType.FAMILY];
  if (category === MonitoringCategory.TELEMETRY) return definition?.[TelemetrySelectorType.FAMILY];
  if (category === MonitoringCategory.CENTRAL_STATIONS) return definition?.[CentralStationsSelectorType.FAMILY];

  return undefined;
}

export interface FamilyVariantResult {
  familyStep: FamilyStep | undefined;
  remainingOptions?: FamilyStepOption[];
  steps?: FamilyStepMap;
  variant:
    | {
        mode: FamilyVariantMode.NO_OPTIONS;
      }
    | {
        mode: FamilyVariantMode.SINGLE_OPTION;
        option: FamilyStepOption;
      }
    | {
        mode: FamilyVariantMode.MULTIPLE_OPTIONS;
        step: FamilyStep;
      };
}

export interface FamilyStepOptionCategoryDefinition<TCategory extends string = string> {
  category: TCategory;
  matchingDeviceTypes: readonly DeviceType[];
}

export enum FamilyStepOptionClassification {
  MATCH = "MATCH",
  NO_MATCH = "NO_MATCH",
  UNKNOWN = "UNKNOWN",
  MIXED = "MIXED",
}

export interface ClassifiedFamilyStepOptions {
  matchedOptions: FamilyStepOption[];
  unmatchedOptions: FamilyStepOption[];
  unknownOptions: FamilyStepOption[];
  mixedOptions: FamilyStepOption[];
}

export interface FamilyVariantClassificationContext {
  variantDeviceTypes: readonly DeviceType[];
  deviceTypesByMatNr: ReadonlyMap<string, DeviceType>;
}

export interface PatientMonitorFamilySteps extends FamilyStepMap {
  sensorStep?: FamilyStep;
  mountingGroupStep?: FamilyStep;
  mountingArmStep?: FamilyStep;
}

export interface TelemetryFamilySteps extends FamilyStepMap {
  sensorStep?: FamilyStep;
  additionalBatteriesStep?: FamilyStep;
}

export interface CentralStationFamilySteps extends FamilyStepMap {
  licenceStep?: FamilyStep;
  accessoriesStep?: FamilyStep;
}

export function isPatientMonitorFamilySteps(steps?: FamilyStepMap): steps is PatientMonitorFamilySteps {
  return steps !== undefined && ("sensorStep" in steps || "mountingGroupStep" in steps || "mountingArmStep" in steps);
}

export function isTelemetryFamilySteps(steps?: FamilyStepMap): steps is TelemetryFamilySteps {
  return steps !== undefined && ("sensorStep" in steps || "additionalBatteriesStep" in steps);
}

export function isCentralStationFamilySteps(steps?: FamilyStepMap): steps is CentralStationFamilySteps {
  return steps !== undefined && ("licenceStep" in steps || "accessoriesStep" in steps);
}

export function resolveFamilyVariantStep(
  family: Family,
  classificationContext?: FamilyVariantClassificationContext,
): FamilyVariantResult {
  const familyStep = family.steps?.[0];
  const familyOptions = familyStep?.options ?? [];
  const variantOptions = classificationContext ? classifyVariantOptions(familyOptions, classificationContext) : familyOptions;
  const remainingOptions = familyOptions.filter((option) => !variantOptions.includes(option));

  if (variantOptions.length === 1) {
    return {
      familyStep,
      remainingOptions,
      variant: {
        mode: FamilyVariantMode.SINGLE_OPTION,
        option: variantOptions[0],
      },
    };
  }

  if (variantOptions.length > 1) {
    return {
      familyStep,
      remainingOptions,
      variant: {
        mode: FamilyVariantMode.MULTIPLE_OPTIONS,
        step: classificationContext
          ? new FamilyStep(
              variantOptions,
              Object.values(StepSelectionType).find((selectionType) => selectionType === familyStep!.stepSelectionType) ??
                StepSelectionType.PICK1,
            )
          : familyStep!,
      },
    };
  }

  return {
    familyStep,
    remainingOptions,
    variant: {
      mode: FamilyVariantMode.NO_OPTIONS,
    },
  };
}

export function resolvePatientMonitorFamilySteps(
  family: Family,
  familyVariant: FamilyVariantResult,
  deviceTypesByMatNr?: ReadonlyMap<string, DeviceType>,
): PatientMonitorFamilySteps {
  if (family.steps.length > 1) {
    return {
      sensorStep: family.steps[1],
      mountingGroupStep: family.steps[2],
      mountingArmStep: family.steps[3],
    };
  }

  if (!deviceTypesByMatNr || !familyVariant.remainingOptions?.length) return {};

  const originalFamilyStep = familyVariant.familyStep;
  if (!originalFamilyStep) return {};

  const deviceTypeDefinition = FAMILY_STEP_DEVICE_TYPES_BY_CATEGORY[MonitoringCategory.PATIENT_MONITORS];
  if (!deviceTypeDefinition) return {};

  const sensorOptions: FamilyStepOption[] = [];
  const mountingGroupOptions: FamilyStepOption[] = [];
  const mountingArmOptions: FamilyStepOption[] = [];

  for (const option of familyVariant.remainingOptions) {
    const deviceTypes = option.devices
      .map((device) => deviceTypesByMatNr.get(String(device)))
      .filter((deviceType): deviceType is DeviceType => deviceType !== undefined);

    if (
      option.subType === SubType.A &&
      deviceTypes.some((deviceType) => deviceTypeDefinition[PatientMonitorSelectorType.MOUNTING_ARM]?.includes(deviceType))
    ) {
      mountingArmOptions.push(option);
    } else if (
      option.subType === SubType.W ||
      deviceTypes.some((deviceType) => deviceTypeDefinition[PatientMonitorSelectorType.MOUNTING_GROUP]?.includes(deviceType))
    ) {
      mountingGroupOptions.push(option);
    } else if (deviceTypes.some((deviceType) => deviceTypeDefinition[PatientMonitorSelectorType.SENSOR]?.includes(deviceType))) {
      sensorOptions.push(option);
    } else if (
      deviceTypes.some((deviceType) => deviceTypeDefinition[PatientMonitorSelectorType.MOUNTING_ARM]?.includes(deviceType))
    ) {
      mountingArmOptions.push(option);
    }
  }

  return {
    sensorStep: createStep(sensorOptions, originalFamilyStep),
    mountingGroupStep: createStep(mountingGroupOptions, originalFamilyStep),
    mountingArmStep: createStep(mountingArmOptions, originalFamilyStep),
  };
}

export function resolveTelemetryFamilySteps(
  family: Family,
  familyVariant: FamilyVariantResult,
  deviceTypesByMatNr?: ReadonlyMap<string, DeviceType>,
): TelemetryFamilySteps {
  if (family.steps.length > 1) {
    return {
      sensorStep: family.steps[1],
      additionalBatteriesStep: family.steps[2],
    };
  }

  if (!deviceTypesByMatNr || !familyVariant.remainingOptions?.length) return {};

  const originalFamilyStep = familyVariant.familyStep;
  if (!originalFamilyStep) return {};

  const deviceTypeDefinition = FAMILY_STEP_DEVICE_TYPES_BY_CATEGORY[MonitoringCategory.TELEMETRY];
  if (!deviceTypeDefinition) return {};

  const originalStepSelectionType = getStepSelectionType(originalFamilyStep);
  if (!originalStepSelectionType) return {};
  const remainingOptionsStep = new FamilyStep(familyVariant.remainingOptions, originalStepSelectionType);

  const sensorOptions = classifyFamilyStepOptions(
    remainingOptionsStep,
    { category: "TELEMETRY_SENSOR", matchingDeviceTypes: deviceTypeDefinition[TelemetrySelectorType.SENSOR] ?? [] },
    deviceTypesByMatNr,
  ).matchedOptions;
  const additionalBatteryOptions = classifyFamilyStepOptions(
    remainingOptionsStep,
    {
      category: "TELEMETRY_ADDITIONAL_BATTERY",
      matchingDeviceTypes: deviceTypeDefinition[TelemetrySelectorType.ADDITIONAL_BATTERIES] ?? [],
    },
    deviceTypesByMatNr,
  ).matchedOptions;

  return {
    sensorStep: createStep(sensorOptions, originalFamilyStep),
    additionalBatteriesStep: createStep(additionalBatteryOptions, originalFamilyStep),
  };
}

export function resolveCentralStationFamilySteps(
  family: Family,
  familyVariant: FamilyVariantResult,
  deviceTypesByMatNr?: ReadonlyMap<string, DeviceType>,
): CentralStationFamilySteps {
  if (family.steps.length > 1) {
    const licenceSourceStep = family.steps.find((step) => step.stepSelectionType === StepSelectionType.PICK1) ?? family.steps[1];
    const licenceOptions =
      licenceSourceStep && deviceTypesByMatNr
        ? classifyFamilyStepOptions(
            licenceSourceStep,
            {
              category: "CENTRAL_STATION_LICENCE",
              matchingDeviceTypes: [DeviceType.CS_LICENCE_M300, DeviceType.CS_LICENCE],
            },
            deviceTypesByMatNr,
          ).matchedOptions
        : (licenceSourceStep?.options ?? []);

    return {
      licenceStep: createStep(licenceOptions, licenceSourceStep ?? family.steps[0]),
      accessoriesStep: family.steps.find((step) => step.stepSelectionType === StepSelectionType.PICK0TON),
    };
  }

  if (!deviceTypesByMatNr || !familyVariant.remainingOptions?.length) return {};

  const originalFamilyStep = familyVariant.familyStep;
  if (!originalFamilyStep) return {};

  const deviceTypeDefinition = FAMILY_STEP_DEVICE_TYPES_BY_CATEGORY[MonitoringCategory.CENTRAL_STATIONS];
  if (!deviceTypeDefinition) return {};

  const stepSelectionType = getStepSelectionType(originalFamilyStep);
  if (!stepSelectionType) return {};

  const remainingOptionsStep = new FamilyStep(familyVariant.remainingOptions, stepSelectionType);
  const licenceOptions = classifyFamilyStepOptions(
    remainingOptionsStep,
    {
      category: "CENTRAL_STATION_LICENCE",
      matchingDeviceTypes: deviceTypeDefinition[CentralStationsSelectorType.LICENCE] ?? [],
    },
    deviceTypesByMatNr,
  ).matchedOptions;
  const accessoryOptions = classifyFamilyStepOptions(
    remainingOptionsStep,
    {
      category: "CENTRAL_STATION_ACCESSORY",
      matchingDeviceTypes: deviceTypeDefinition[CentralStationsSelectorType.ACCESSORIES] ?? [],
    },
    deviceTypesByMatNr,
  ).matchedOptions;

  return {
    licenceStep: createStep(licenceOptions, originalFamilyStep),
    accessoriesStep: createStep(accessoryOptions, originalFamilyStep),
  };
}

function createStep(options: FamilyStepOption[], originalFamilyStep: FamilyStep): FamilyStep | undefined {
  const stepSelectionType = getStepSelectionType(originalFamilyStep);
  return options.length && stepSelectionType ? new FamilyStep(options, stepSelectionType) : undefined;
}

function getStepSelectionType(step: FamilyStep): StepSelectionType | undefined {
  return Object.values(StepSelectionType).find((selectionType) => selectionType === step.stepSelectionType);
}

function classifyVariantOptions(
  options: FamilyStepOption[],
  classificationContext: FamilyVariantClassificationContext,
): FamilyStepOption[] {
  const matchingDeviceTypes = new Set(classificationContext.variantDeviceTypes);

  return options.filter((option) => {
    const classification = classifyFamilyStepOption(option, matchingDeviceTypes, classificationContext.deviceTypesByMatNr);

    return (
      classification === FamilyStepOptionClassification.MATCH ||
      (matchingDeviceTypes.has(DeviceType.CS_BASE) && classification === FamilyStepOptionClassification.MIXED)
    );
  });
}

export function classifyFamilyStepOptions<TCategory extends string>(
  step: FamilyStep,
  categoryDefinition: FamilyStepOptionCategoryDefinition<TCategory>,
  deviceTypesByMatNr: ReadonlyMap<string, DeviceType>,
): ClassifiedFamilyStepOptions {
  const matchingDeviceTypes = new Set(categoryDefinition.matchingDeviceTypes);
  const result: ClassifiedFamilyStepOptions = {
    matchedOptions: [],
    unmatchedOptions: [],
    unknownOptions: [],
    mixedOptions: [],
  };

  for (const option of step.options ?? []) {
    const classification = classifyFamilyStepOption(option, matchingDeviceTypes, deviceTypesByMatNr);

    if (classification === FamilyStepOptionClassification.MATCH) result.matchedOptions.push(option);
    if (classification === FamilyStepOptionClassification.NO_MATCH) result.unmatchedOptions.push(option);
    if (classification === FamilyStepOptionClassification.UNKNOWN) result.unknownOptions.push(option);
    if (classification === FamilyStepOptionClassification.MIXED) result.mixedOptions.push(option);
  }

  return result;
}

function classifyFamilyStepOption(
  option: FamilyStepOption,
  matchingDeviceTypes: ReadonlySet<DeviceType>,
  deviceTypesByMatNr: ReadonlyMap<string, DeviceType>,
): FamilyStepOptionClassification {
  const resolvedTypes = option.devices.map((device) => deviceTypesByMatNr.get(String(device)));

  if (!resolvedTypes.length || resolvedTypes.some((deviceType) => deviceType === undefined)) {
    return FamilyStepOptionClassification.UNKNOWN;
  }

  const knownTypes = resolvedTypes.filter((deviceType): deviceType is DeviceType => deviceType !== undefined);
  const hasMatchingType = knownTypes.some((deviceType) => matchingDeviceTypes.has(deviceType));
  const hasOtherType = knownTypes.some((deviceType) => !matchingDeviceTypes.has(deviceType));

  if (hasMatchingType && hasOtherType) return FamilyStepOptionClassification.MIXED;
  if (hasMatchingType) return FamilyStepOptionClassification.MATCH;
  return FamilyStepOptionClassification.NO_MATCH;
}
